import {
  assign,
  createStateMachine,
  interpret,
  type InvokeDoneEvent,
  type InvokeErrorEvent,
} from '@heidi/flow/state-machine';
import {
  normalizeCaptureStartFailure,
  type CaptureStartFailure,
  type CaptureStopFailure,
  type RecordingStartOutcome,
} from './recording-start-outcome';

type TimerCompletion = 'started' | 'cancelled';
type StartContext = {
  captureFailure: CaptureStartFailure;
  timerCompletion: TimerCompletion;
  stopError?: CaptureStopFailure;
};

// This actor lives for one start attempt only. Capture/store state remains the
// authority for the full recording lifecycle.
export const recordingStartMachine = createStateMachine({
  id: 'recordingStart',
  initial: 'startCapture',
  context: {
    captureFailure: { type: 'capture-failed', code: 'unexpected-capture-error' },
    timerCompletion: 'started',
  } as StartContext,
  states: {
    startCapture: {
      invoke: {
        src: 'startCapture',
        onDone: 'startTimer',
        onError: {
          target: 'captureFailed',
          actions: assign<StartContext, InvokeErrorEvent>((_, event) => ({
            captureFailure: normalizeCaptureStartFailure(event.error),
          })),
        },
      },
    },
    startTimer: {
      invoke: {
        src: 'startTimer',
        onDone: {
          target: 'timerSettled',
          actions: assign<StartContext, InvokeDoneEvent<TimerCompletion>>((_, event) => ({
            timerCompletion: event.data,
          })),
        },
        onError: 'stopCapture',
      },
    },
    stopCapture: {
      invoke: {
        src: 'stopCapture',
        onDone: 'confirmCaptureStopped',
        onError: {
          target: 'confirmCaptureStopped',
          // Preserve failure evidence without retaining the raw rejection.
          actions: assign<StartContext>(() => ({
            stopError: { code: 'capture-stop-rejected' },
          })),
        },
      },
    },
    confirmCaptureStopped: {
      invoke: {
        src: 'confirmCaptureStopped',
        onDone: 'setupFailedCaptureStopped',
        onError: 'setupFailedStopUnconfirmed',
      },
    },
    timerSettled: { type: 'final' },
    captureFailed: { type: 'final' },
    setupFailedCaptureStopped: { type: 'final' },
    setupFailedStopUnconfirmed: { type: 'final' },
  },
});

type TerminalStartState =
  | 'timerSettled'
  | 'captureFailed'
  | 'setupFailedCaptureStopped'
  | 'setupFailedStopUnconfirmed';

export function toRecordingStartOutcome(
  state: TerminalStartState,
  captureFailure: CaptureStartFailure,
  timerCompletion: TimerCompletion = 'started',
  stopError?: CaptureStopFailure
): RecordingStartOutcome {
  switch (state) {
    case 'timerSettled':
      return { type: timerCompletion };
    case 'captureFailed':
      return captureFailure;
    case 'setupFailedCaptureStopped':
      return {
        type: 'setup-failed-capture-stopped',
        setupError: { code: 'timer-start-failed' },
        ...(stopError ? { stopError } : {}),
      };
    case 'setupFailedStopUnconfirmed':
      return {
        type: 'setup-failed-stop-unconfirmed',
        setupError: { code: 'timer-start-failed' },
        stopError: {
          code: 'capture-stop-unconfirmed',
          ...(stopError ? { cause: stopError.code } : {}),
        },
      };
  }
}

export type RecordingStartPorts = {
  startCapture: () => Promise<void>;
  startTimer: () => TimerCompletion | Promise<TimerCompletion>;
  stopCapture: () => Promise<void>;
  isCaptureStopped: () => boolean;
};

export function runRecordingStart(
  ports: RecordingStartPorts
): Promise<RecordingStartOutcome> {
  return new Promise((resolve) => {
    const actor = interpret(recordingStartMachine, {
      services: {
        startCapture: ports.startCapture,
        startTimer: ports.startTimer,
        stopCapture: ports.stopCapture,
        confirmCaptureStopped: () => {
          // Rejection can follow supported teardown; resolution may deduplicate
          // while another stop is running. Neither proves cessation on its own.
          // Only a fresh authoritative snapshot confirms it (throws fail closed).
          if (!ports.isCaptureStopped()) throw new Error('Capture stop unconfirmed');
        },
      },
    });
    const unsubscribe = actor.subscribe((state) => {
      switch (state.value) {
        case 'timerSettled':
        case 'captureFailed':
        case 'setupFailedCaptureStopped':
        case 'setupFailedStopUnconfirmed':
          unsubscribe();
          actor.stop();
          resolve(
            toRecordingStartOutcome(
              state.value,
              state.context.captureFailure,
              state.context.timerCompletion,
              state.context.stopError
            )
          );
      }
    });
    actor.start();
  });
}
